import {
    limit as limitFirebase,
    orderBy,
    where
} from "firebase/firestore"
import { getCliente, getEstacionamento } from '../../config/auth'
import { Collection, TicketStatus, WhereField } from '../../shared/constants'
import { chunkItemsByQuantity, generateUUIDV7, isNullOrEmpty } from '../../shared/utils'
import { addDoc, deleteDoc, getAllDocsByQueries, getCountByQueries, getDocById, getDocByQueries, updateDoc } from './collectionBaseWorker'

export const addTicket = async ({ data }) => {
    const doc = await addDoc({ collection: Collection.TICKET, data: data })
    return doc.id
}

export const getTicketById = async ({ id }) => {
    return await getDocById({ collection: Collection.TICKET, id: id })
}

export const getRandomNumberInTicketByParkId = async ({ estacionamentoId }) => {
    const number = parseInt(Math.random() * 999999).toString().padEnd(6, "0")
    const exist = await getTicketByNumberAndParkId({ ticket: number, parkId: estacionamentoId })
    if (exist) {
        return await getRandomNumberInTicketByParkId({ estacionamentoId: estacionamentoId })
    }
    return number
}

export const ticketsByMonthlyId = async ({ monthlyId }) => {
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.MONTHLY__ID, "==", monthlyId)
        ]
    })
}

export const getTicketsByPartnerIdAndCompanyId = async ({ partnerId, companyId }) => {
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where("relationshipApplied.id", "==", partnerId),
            where("relationshipApplied.companyId", "==", companyId)
        ]
    })
}

export const ticketsParkedByMonthlyId = async ({ monthlyId }) => {
    const items = await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.MONTHLY__ID, "==", monthlyId),
            where(WhereField.CAIXA_FECHADO, "==", false)
        ]
    })
    return items.filter(e => e.status == TicketStatus.PARKING || e.status == TicketStatus.PAYED_AND_PARKING || e.status == TicketStatus.PAYED_AND_WAITING_BACK)
}

export const ticketsParkedByAccreditedId = async ({ accreditedId }) => {
    const items = await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.ACCREDITED__ID, "==", accreditedId),
            where(WhereField.CAIXA_FECHADO, "==", false)
        ]
    })
    return items.filter(e => e.status == TicketStatus.PARKING || e.status == TicketStatus.PAYED_AND_PARKING || e.status == TicketStatus.PAYED_AND_WAITING_BACK)
}

export const ticketsByAccreditedId = async ({ accreditedId }) => {
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.ACCREDITED__ID, "==", accreditedId)
        ]
    })
}

export const removeTicketById = async ({ id }) => {
    await deleteDoc({ collection: Collection.TICKET, id: id })
}

export const updateTicket = async (id, data) => {
    data.deviceIdentifier = generateUUIDV7()
    await updateDoc({ collection: Collection.TICKET, id: id, data: data })
}

export const ticketsByPlate = async ({ placa }) => {
    const parkId = getEstacionamento()?.id
    if (parkId) {
        return await getAllDocsByQueries({
            collection: Collection.TICKET,
            queries: [
                where(WhereField.PARKID, "==", parkId),
                where(WhereField.PLACA, "==", placa)
            ]
        })
    }
}

export const ticketsByPlateAndClientId = async ({ placa }) => {
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.CLIENTEID, "==", getCliente()?.id),
            where(WhereField.PLACA, "==", placa)
        ]
    })
}

export const ticketsByNumberAndClientId = async ({ number, clienteId }) => {
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(Collection.TICKET, '==', number),
            where(WhereField.CLIENTEID, "==", clienteId)
        ]
    })
}

export const ticketsByCameraInWithIdentifierAndParkId = async ({ identifier, estacionamentoId, limit }) => {
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.PARKID, "==", estacionamentoId),
            where(WhereField.CAMERA__ID, "==", identifier),
            limitFirebase(limit ?? 200)
        ]
    })
}

export const ticketsByCameraOutWithIdentifierAndParkId = async ({ identifier, estacionamentoId, limit }) => {
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.PARKID, "==", estacionamentoId),
            where(WhereField.CAMERA__OUT, "==", identifier),
            limitFirebase(limit ?? 200)
        ]
    })
}

export const getTicketsByClientId = async (clienteId) => {
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.CLIENTEID, "==", clienteId)
        ]
    })
}

export const getTicketByNumberAndParkId = async ({ ticket, parkId }) => {
    return await getDocByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.TICKET, "==", String(ticket)),
            where(WhereField.PARKID, "==", parkId),
            where(WhereField.CAIXA_FECHADO, "==", false)
        ]
    })
}

export const getTicketsByNumberAndClienteId = async ({ ticket, clienteId }) => {
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.TICKET, "==", String(ticket)),
            where(WhereField.CLIENTEID, "==", clienteId)
        ]
    })
}

export const getTicketByPlateAndParkId = async ({ plate, parkId }) => {
    return await getDocByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.PLACA, "==", plate.toUpperCase()),
            where(WhereField.PARKID, "==", parkId),
            where(WhereField.CAIXA_FECHADO, "==", false)
        ]
    })
}

export const getTicketsByPlateAndClientId = async ({ plate, clienteId }) => {
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.PLACA, "==", plate.toUpperCase()),
            where(WhereField.CLIENTEID, "==", clienteId)
        ]
    })
}

export const getTicketByNoteIdentifier = async ({ identifier }) => {
    return await getDocByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.NEW_NOTE_IDENTIFIER, "==", identifier)
        ]
    })
}

export const getTicketsOpenByClientId = async ({ clienteId }) => {
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.CLIENTEID, "==", clienteId),
            where(WhereField.CAIXA_FECHADO, "==", false)
        ]
    })
}

export const getTicketsByCashierId = async ({ estacionamentoId, caixaId }) => {
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.PARKID, "==", estacionamentoId),
            where(WhereField.CAIXAS_IDS, "array-contains-any", [caixaId])
        ]
    })
}

export const getTicketsOpenByPlateAndClienteId = async ({ plate, clienteId }) => {
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.CLIENTEID, "==", clienteId),
            where(WhereField.PLACA, "==", plate.toUpperCase()),
            where(WhereField.CAIXA_FECHADO, "==", false)
        ]
    })
}

export const getTicketOpenByNumberAndClienteId = async ({ number, clienteId }) => {
    return await getDocByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.TICKET, "==", String(number)),
            where(WhereField.CLIENTEID, "==", clienteId),
            where(WhereField.CAIXA_FECHADO, "==", false)
        ]
    })
}

export const getAllLastTickets = async ({ limit, associatedRessales }) => {
    if (isNullOrEmpty(associatedRessales)) {
        return []
    }
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.REVENDAID, "in", associatedRessales.map(e => e.value)),
            orderBy(WhereField.CREATE_AT, "desc"),
            limitFirebase(limit ?? 50)
        ]
    })
}

export const getAllRequestedTickets = async ({ estacionamentoId }) => {
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.PARKID, "==", estacionamentoId),
            where(WhereField.REQUESTED, "==", true)
        ]
    })
}

export const getTicketsOpenByParkId = async ({ estacionamentoId }) => {
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.PARKID, "==", estacionamentoId),
            where(WhereField.CAIXA_FECHADO, "==", false)
        ]
    })
}

export const getTicketsOpenByParksIds = async ({ ids }) => {
    var result = []
    const chunckIds = chunkItemsByQuantity({ items: ids })
    for (const ids of chunckIds) {
        const resultChunk = await getAllDocsByQueries({
            collection: Collection.TICKET,
            queries: [
                where(WhereField.PARKID, "in", ids),
                where(WhereField.CAIXA_FECHADO, "==", false)
            ]
        })
        result.push(...resultChunk)
    }
    return result
}

export const getAllTicketsOpenByParkId = async ({ estacionamentoId }) => {
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.PARKID, "==", estacionamentoId),
            where(WhereField.STATUS, "in", ["Estacionado", "Pago e Estacionado", "Pago e Aguardando"]),
            where(WhereField.CAIXA_FECHADO, "==", false)
        ]
    })
}

export const getQuantityTicketsOpenByParkId = async ({ estacionamentoId }) => {
    return await getCountByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.PARKID, "==", estacionamentoId),
            where(WhereField.STATUS, "in", ["Estacionado", "Pago e Estacionado", "Pago e Aguardando"]),
            where(WhereField.CAIXA_FECHADO, "==", false)
        ]
    })
}

export const getAllTicketsByAccreditedIdAndDates = async ({ startDate, endDate, accreditedId }) => {
    const items = await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.CREATE_AT, '>=', startDate),
            where(WhereField.CREATE_AT, '<=', endDate),
            where(WhereField.ACCREDITED__ID, '==', accreditedId)
        ]
    })
    return items.sort((a, b) => a.createAt?.toDate() - b.createAt?.toDate())
}

export const getAllTicketsByContactInfo = async ({ contactInfoId }) => {
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.CONTACT_INFO__ID, '==', contactInfoId)
        ]
    })
}

export const getAllTicketsByPlateAndParkId = async ({ plate, parkId }) => {
    return await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.PLACA, '==', plate.toUpperCase()),
            where(WhereField.PARKID, '==', parkId)
        ]
    })
}

export const getAllTicketsByDatesInRange = async ({ startDate, endDate, estacionamentosIds }) => {
    var result = []
    const chunckIds = chunkItemsByQuantity({ items: estacionamentosIds })
    for (const ids of chunckIds) {
        const resultChunk = await getAllDocsByQueries({
            collection: Collection.TICKET,
            queries: [
                where(WhereField.CREATE_AT, '>=', startDate),
                where(WhereField.CREATE_AT, '<=', endDate),
                where(WhereField.PARKID, 'in', ids)
            ]
        })
        result.push(...resultChunk)
    }
    return result.sort((a, b) => a.changeDate?.toDate() - b.changeDate?.toDate())
}

export const getAllTicketsOpenByStatusAndParkId = async ({ status, parkId }) => {
    const items = await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.PARKID, "==", parkId),
            where(WhereField.CAIXA_FECHADO, "==", false),
            where(WhereField.STATUS, "in", status)
        ]
    })
    return items.sort((a, b) => a.createAt?.toDate() - b.createAt?.toDate())
}

export const ticketsByPlates = async ({ placas }) => {
    var tickets = []
    for (const placa of placas) {
        const result = await ticketsByPlate({ placa: placa })
        tickets.push(result)
    }
    return tickets.flatMap(e => e)
}

export const ticketsByPlatesAndClientId = async ({ placas }) => {
    var tickets = []
    for (const placa of placas) {
        const result = await ticketsByPlateAndClientId({ placa: placa })
        tickets.push(result)
    }
    return tickets.flatMap(e => e)
}

export const updateTicketById = async ({ id, data }) => {
    await updateDoc({ collection: Collection.TICKET, id: id, data: data })
}


export const getAllTicketsByCliendIdAndStatusOrModel = async ({ clienteId, status, model, limit }) => {
    const items = await getAllDocsByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.CLIENTEID, '==', clienteId),
            !isNullOrEmpty(status) && where(WhereField.STATUS, '==', status),
            !isNullOrEmpty(model) && where('vehicler.modelo', '==', model.split(" ").shift().join(" ").charAt(0).toUpperCase() + model.join(" ").slice(1)),
            orderBy(WhereField.CREATE_AT, 'desc'),
            limitFirebase(limit ?? 50)
        ]
    })
    return items
}

export const getCountTicketsByParkIdAndDates = async ({ estacionamentoId, startDate, endDate }) => {
    return await getCountByQueries({
        collection: Collection.TICKET,
        queries: [
            where(WhereField.PARKID, "==", estacionamentoId),
            where(WhereField.CREATE_AT, '>=', startDate),
            where(WhereField.CREATE_AT, '<=', endDate)
        ]
    })
}